import { useQuery } from "@apollo/client";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import Spinner from "@src/components/spinner";
import { FileText, MapPin, ChevronRight, Clock, Settings, ArrowLeft, QrCode, Nfc, List, MapPinnedIcon, ExternalLink, AlertTriangle } from "lucide-react";
import { faArrowLeftLong, faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MapComponent from "@src/components/mapComponent/mapComponent";
import { Checkbox, Empty, Input, Drawer, Typography, Tag, Tooltip, Tabs } from "antd";
import { getAllTaskInLibrary } from '@src/util/query/taskInLibrary.query';
import { BranchesOutlined, ClockCircleOutlined, EnvironmentOutlined } from "@ant-design/icons";
import Swal from 'sweetalert2';
import { getSiteQuery } from "@src/util/query/site.query";
import { getSiteGroupLocationBySiteGroupIdQuery } from "@src/util/query/sitegrouplocation.query";
import { LocationSelector } from "@src/components/LocationSelector/LocationSelector.component";
import { getSiteCheckpointsQuery } from "@src/util/query/siteCheckpoint.query";
import { CreateTaskFieldDrawer } from "@src/components/createTask";
import { CheckpointDrawer } from "@src/pages/site/siteDetailPage/component/siteCheckpoints/checkpoint.drawer";


export const TaskAssignmentModalGeneric = ({
    visible, close,
    selectedTasks, setSelectedTasks,
    selectedCheckpoints = [], setSelectedCheckpoints = () => {},
    standAlone = false, handelSubmit = () => {}, loading = false,
    sitegroupId, associatedSiteGroupLocationId, checkpointDisabled=false,
    siteId, shiftStartTime, shiftEndTime,
    // Lets a caller with separate "Assign Task" / "Assign Checkpoint" entry points open
    // this modal already focused on the right one, instead of always landing on Tasks.
    initialTab = 'tasks',
    // When the caller has two separate single-purpose buttons (Add Duty's "Assign Task"
    // and "Assign Checkpoint"), each should only ever show its own tab - not let the user
    // wander into the other one. Callers with a single generic "Add Task" entry point
    // (addRota, editDutyTaskTab, editDutyActivityTab) leave this false and keep both tabs.
    lockInitialTab = false,
}) => {


    // ── Duty duration (from shift start/end) ───────────────────────────────────────
    const hasShiftTimes = !!shiftStartTime && !!shiftEndTime;
    const dutyDurationHours = useMemo(() => {
        if (!hasShiftTimes) return null;
        const start = dayjs(`2000-01-01 ${shiftStartTime}`, "YYYY-MM-DD HH:mm");
        let end = dayjs(`2000-01-01 ${shiftEndTime}`, "YYYY-MM-DD HH:mm");
        if (!end.isAfter(start)) end = end.add(1, 'day');
        return end.diff(start, 'minute') / 60;
    }, [shiftStartTime, shiftEndTime, hasShiftTimes]);

    // A checkpoint's start time must fall on/after the shift start and on/before the shift end
    // (handles overnight shifts, e.g. 22:00 → 06:00, the same way dutyDurationHours does).
    const isStartTimeWithinDuty = (timeStr) => {
        if (!hasShiftTimes || !timeStr) return false;
        const start = dayjs(`2000-01-01 ${shiftStartTime}`, "YYYY-MM-DD HH:mm");
        let end = dayjs(`2000-01-01 ${shiftEndTime}`, "YYYY-MM-DD HH:mm");
        if (!end.isAfter(start)) end = end.add(1, 'day');
        let t = dayjs(`2000-01-01 ${timeStr}`, "YYYY-MM-DD HH:mm");
        if (t.isBefore(start)) t = t.add(1, 'day');
        return !t.isBefore(start) && !t.isAfter(end);
    };

    // A custom task time window is valid when both ends fall inside the duty and end is after start
    // (handles overnight shifts by rolling times before shift start to the next day, like above).
    const isCustomWindowValid = (from, to) => {
        if (!isStartTimeWithinDuty(from) || !isStartTimeWithinDuty(to)) return false;
        const start = dayjs(`2000-01-01 ${shiftStartTime}`, "YYYY-MM-DD HH:mm");
        let f = dayjs(`2000-01-01 ${from}`, "YYYY-MM-DD HH:mm");
        if (f.isBefore(start)) f = f.add(1, 'day');
        let t = dayjs(`2000-01-01 ${to}`, "YYYY-MM-DD HH:mm");
        if (t.isBefore(start)) t = t.add(1, 'day');
        return t.isAfter(f);
    };

    // ── Draft selections — only flushed to parent state when "Assign" is clicked ────
    const [draftTasks, setDraftTasks] = useState(selectedTasks || []);
    const [draftCheckpoints, setDraftCheckpoints] = useState(selectedCheckpoints || []);
    useEffect(() => {
        if (visible) {
            setDraftTasks(selectedTasks || []);
            setDraftCheckpoints(selectedCheckpoints || []);
        }
    }, [visible]);

    const { loading: taskListLoading, data: taskListData, refetch: refetchTaskList } = useQuery(getAllTaskInLibrary, {
        variables: { input: { groupId: null, isArchived: false, siteId } },
        skip: !siteId,
        fetchPolicy: 'network-only'
    });

    const { data: currentSiteData } = useQuery(getSiteQuery, {
        variables: { id: siteId },
        skip: !siteId,
        fetchPolicy: 'cache-and-network'
    });

    const [createTaskDrawerVisible, setCreateTaskDrawerVisible] = useState(false);
    const [createCheckpointDrawerVisible, setCreateCheckpointDrawerVisible] = useState(false);

    const { data: siteGroupLocationData, loading: siteGroupLocationLoading } = useQuery(getSiteGroupLocationBySiteGroupIdQuery, {
        variables: { siteGroupId: sitegroupId },
        fetchPolicy: 'network-only'
    });

    const { data: checkpointData, loading: checkpointLoading, refetch: refetchCheckpointList } = useQuery(getSiteCheckpointsQuery, {
        variables: { siteId },
        skip: !siteId,
        fetchPolicy: 'network-only'
    });

    const [sitegroupLocations, setSiteGroupLocations] = useState([]);
    useEffect(() => {
        if (siteGroupLocationData?.getSiteGroupLocationBySiteGroupId?.response && !siteGroupLocationLoading) {
            setSiteGroupLocations(siteGroupLocationData.getSiteGroupLocationBySiteGroupId.response);
        }
    }, [siteGroupLocationData?.getSiteGroupLocationBySiteGroupId?.response, siteGroupLocationLoading]);

    const [taskList, setTaskList] = useState([]);
    useEffect(() => {
        if (taskListData?.getAllTaskInLibrary?.response && !taskListLoading) {
            setTaskList(taskListData.getAllTaskInLibrary.response);
        }
    }, [taskListData?.getAllTaskInLibrary?.response, taskListLoading]);

    const checkpointList = checkpointData?.getSiteCheckpoints?.response || [];

    // ── Active item tracking ──────────────────────────────────────────────────────
    // activeItemType: 'task' | 'checkpoint' | null
    const [activeTaskId, setActiveTaskId] = useState(null);
    const [activeCheckpointId, setActiveCheckpointId] = useState(null);
    const [activeItemType, setActiveItemType] = useState(null);

    const [location, setLocation] = useState({ display: "", lat: "", lng: "" });
    const [configStep, setConfigStep] = useState('select');
    // Location pin is optional and the map picker is the heaviest thing on this screen -
    // keep it collapsed until asked for instead of mounting it for every task by default.
    const [showLocationPicker, setShowLocationPicker] = useState(false);
    // Collapse the map back down whenever a different task becomes active, so it doesn't
    // stay open (or stay hidden) based on whatever the previously-configured task left it at.
    useEffect(() => { setShowLocationPicker(false); }, [activeTaskId]);
    const [checkpointFrequency, setCheckpointFrequency] = useState({ type: null, value: null });
    const [freqUnit, setFreqUnit] = useState('hours');
    const [freqRawInput, setFreqRawInput] = useState('');
    const [checkpointStartTime, setCheckpointStartTime] = useState(null);
    const [activeTab, setActiveTab] = useState(initialTab);

    useEffect(()=>{
        if(draftCheckpoints?.length>0){
            setFreqRawInput(draftCheckpoints[0]?.frequencyValue)
            if (draftCheckpoints[0]?.frequencyType && draftCheckpoints[0]?.frequencyValue) {
                setCheckpointFrequency({ type: draftCheckpoints[0].frequencyType, value: draftCheckpoints[0].frequencyValue });
            }
            if (draftCheckpoints[0]?.from) {
                setCheckpointStartTime(draftCheckpoints[0].from);
            }
        }
    },[draftCheckpoints]);

    const { Text } = Typography;

    // Normalised active item — same shape regardless of type
    const activeTask = draftTasks.find(t => t?.taskId === activeTaskId);
    const activeCheckpointEntry = draftCheckpoints.find(c => c?.checkpointId === activeCheckpointId);
    const activeItem = activeItemType === 'checkpoint' ? activeCheckpointEntry : activeTask;

    // Update whichever item type is currently active
    const updateActiveItemDetails = (updates) => {
        if (activeItemType === 'checkpoint') {
            setDraftCheckpoints(prev => prev.map(c =>
                c?.checkpointId === activeCheckpointId ? { ...c, ...updates } : c
            ));
        } else {
            setDraftTasks(draftTasks.map(t =>
                t?.taskId === activeTaskId ? { ...t, ...updates } : t
            ));
        }
    };

    const clearActiveItem = () => {
        setActiveTaskId(null);
        setActiveCheckpointId(null);
        setActiveItemType(null);
        setConfigStep('select');
    };

    const handleTabChange = (tab) => {
        if (!hasShiftTimes) { setActiveTab(tab); return; }
        setActiveTab(tab);
        if (tab === 'checkpoints') {
            if (checkpointList.length > 0) {
                if (draftCheckpoints.length === 0) {
                    const freq = checkpointFrequency.type ? { frequencyType: checkpointFrequency.type, frequencyValue: checkpointFrequency.value } : {};
                    const startTime = checkpointStartTime || shiftStartTime;
                    setDraftCheckpoints(checkpointList.map(cp => ({
                        checkpointId: cp._id, label: cp.name,
                        associatedSiteGroupLocationId: selectedLocation ? selectedLocation?._id : null,
                        from: startTime,
                        ...freq
                    })));
                    setCheckpointStartTime(startTime);

                    // Short heads-up right when the tab opens with an unconfigured
                    // NFC checkpoint auto-selected, instead of leaving it to the
                    // (longer) footer warning to be noticed.
                    const unconfigured = checkpointList.filter(cp => cp.tagType === 'nfc' && !cp.nfcTagId);
                    if (unconfigured.length > 0) {
                        Swal.fire({
                            icon: 'warning',
                            title: `${unconfigured.length} checkpoint${unconfigured.length > 1 ? 's' : ''} not configured`,
                            text: `${unconfigured.map(cp => cp.name).join(', ')} ${unconfigured.length > 1 ? 'need' : 'needs'} an NFC tag scan from the mobile app before assigning.`,
                            confirmButtonText: 'OK',
                            customClass: { confirmButton: 'swal-theme-confirm-btn' }
                        });
                    }
                }
                setActiveItemType('checkpoint');
                setConfigStep('checkpoint-frequency');
            } else {
                setActiveItemType(null);
                setConfigStep('select');
            }
        } else if (tab === 'tasks') {
            if (activeTaskId) {
                setActiveItemType('task');
                setConfigStep('action');
            } else {
                setActiveItemType(null);
                setConfigStep('select');
            }
        }
    };

    const handleCheckpointSelection = (cp, checked) => {
        if (!hasShiftTimes) return;
        if (checked) {
            const freq = checkpointFrequency.type ? { frequencyType: checkpointFrequency.type, frequencyValue: checkpointFrequency.value } : {};
            const startTime = checkpointStartTime || shiftStartTime;
            setDraftCheckpoints(prev => [...prev, {
                checkpointId: cp._id, label: cp.name,
                associatedSiteGroupLocationId: selectedLocation ? selectedLocation?._id : null,
                from: startTime,
                ...freq
            }]);
            setCheckpointStartTime(startTime);
            // Same as the "Select all" checkbox: checking one checkpoint should open the
            // frequency panel on the right, not leave it sitting on the empty placeholder.
            setActiveItemType('checkpoint');
            setConfigStep('checkpoint-frequency');
        } else {
            setDraftCheckpoints(prev => prev.filter(c => c.checkpointId !== cp._id));
            if (activeCheckpointId === cp._id) clearActiveItem();
        }
    };

    useEffect(() => {
        if (configStep === "site") {
            Swal.fire({
                icon: 'info',
                title: 'Automated Shift Creation',
                html: `
                    <div style="text-align: left; padding: 10px;">
                        <p style="margin-bottom: 10px;">This will create an <strong>automated shift</strong> on the selected site based on the task completion actual time.</p>
                        <p style="margin-bottom: 10px;"><strong>Benefits:</strong></p>
                        <ul style="margin-left: 20px; margin-bottom: 10px;">
                            <li>Accurate pay calculation</li>
                            <li>Transparent billing</li>
                            <li>Automatic record keeping</li>
                            <li>Real-time tracking</li>
                        </ul>
                    </div>
                `,
                confirmButtonText: 'Got it!',
                confirmButtonColor: '#1890ff',
                width: 500
            });
        }
    }, [configStep]);

    // ── Task selection ────────────────────────────────────────────────────────────
    const handleTaskSelection = (taskId, label, checked) => {
        if (!hasShiftTimes) return;
        if (checked) {
            setDraftTasks([...draftTasks, {
                taskId, label,
                assignmentType: null, priority: null,
                associatedSiteId: null, from: null, to: null, location: null,
                associatedSiteGroupLocationId: selectedLocation ? selectedLocation?._id : null
            }]);
            setActiveTaskId(taskId);
            setActiveCheckpointId(null);
            setActiveItemType('task');
            setConfigStep('action');
        } else {
            setDraftTasks(draftTasks.filter(t => t?.taskId !== taskId));
            if (activeTaskId === taskId) clearActiveItem();
        }
    };

    const handleLocationUpdate = () => {
        const locationData = sessionStorage.getItem("location");
        if (locationData) {
            updateActiveItemDetails({ location: JSON.parse(locationData) });
            sessionStorage.removeItem("location");
        }
    };

    // Checkpoints whose physical NFC tag hasn't been configured yet (scanned/
    // written from the mobile app) can't actually be patrolled - block assigning
    // them to a duty rather than let it fail silently for the guard on shift.
    // Any system user has the mobile-app privilege to configure a checkpoint for
    // any site, so the message surfaced from this points them there instead of
    // just refusing.
    const unconfiguredNfcCheckpoints = draftCheckpoints
        .map(dc => checkpointList.find(c => c._id === dc.checkpointId))
        .filter(cp => cp?.tagType === 'nfc' && !cp?.nfcTagId);

    // ── Submit disabled check ─────────────────────────────────────────────────────
    const isSubmitDisabled = () => {
        if (!hasShiftTimes) return true;
        if (draftTasks.length === 0 && draftCheckpoints.length === 0) return true;
        if (draftTasks.some(t => !t.assignmentType)) return true;
        if (draftTasks.some(t => t.assignmentType === 'custom' && !isCustomWindowValid(t.from, t.to))) return true;
        if (draftCheckpoints.length > 0) {
            if (!checkpointFrequency.type || !checkpointFrequency.value) return true;
            if (dutyDurationHours != null && checkpointFrequency.value > dutyDurationHours) return true;
            if (!checkpointStartTime || !isStartTimeWithinDuty(checkpointStartTime)) return true;
            if (unconfiguredNfcCheckpoints.length > 0) return true;
        }
        return false;
    };

    const unconfiguredTaskCount = draftTasks.filter(t => !t.assignmentType || (t.assignmentType === 'custom' && !isCustomWindowValid(t.from, t.to))).length;

    const getItemSummary = (item, isCheckpoint = false) => {
        if (isCheckpoint) {
            if (!checkpointFrequency.type || !freqRawInput) return null;
            const unitLabel = freqUnit === 'minutes' ? 'min' : 'hr';
            const label = `Every ${freqRawInput} ${unitLabel}`;
            return (
                <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {checkpointStartTime && (
                        <Tag color="blue" style={{ fontSize: '10px' }}>
                            <Clock size={10} /> From {checkpointStartTime}
                        </Tag>
                    )}
                    <Tag color="cyan" style={{ fontSize: '10px' }}>
                        <ClockCircleOutlined style={{ fontSize: '10px' }} /> {label}
                    </Tag>
                </div>
            );
        }
        const TIMING_LABELS = {
            shift_start: { label: 'Start of Shift' },
            shift_end: { label: 'End of Shift' },
            anytime: { label: 'Any Time' },
            ...(!standAlone ? { both_start_end: { label: 'Both Start & End' } } : {}),
            custom: { label: (item.from && item.to) ? `${item.from} – ${item.to}` : 'Custom time' },
        };
        const tags = [];
        if (item.assignmentType && TIMING_LABELS[item.assignmentType]) {
            const { label } = TIMING_LABELS[item.assignmentType];
            tags.push(
                <Tag key="timing" style={{ fontSize: '10px', color: 'rgb(13,92,99)', borderColor: 'rgba(13,92,99,0.3)', background: 'rgba(13,92,99,0.06)' }}>
                    <ClockCircleOutlined style={{ fontSize: '10px' }} /> {label}
                </Tag>
            );
        }
        return tags.length > 0 ? (
            <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>{tags}</div>
        ) : null;
    };

    // ── Checkpoint frequency config ───────────────────────────────────────────────
    const renderCheckpointFrequencyConfig = () => {
        const exceedsDuration = (rawValue, unit) => {
            const num = parseFloat(rawValue);
            if (isNaN(num)) return false;
            const valueInHours = unit === 'minutes' ? num / 60 : num;
            return dutyDurationHours != null && valueInHours > dutyDurationHours;
        };

        const applyFrequency = (rawValue, unit) => {
            const num = parseFloat(rawValue);
            if (!rawValue || isNaN(num) || num <= 0) return;
            const valueInHours = unit === 'minutes' ? parseFloat((num / 60).toFixed(4)) : num;
            if (exceedsDuration(rawValue, unit)) {
                setCheckpointFrequency({ type: null, value: null });
                return;
            }
            setCheckpointFrequency({ type: 'interval', value: valueInHours });
            setDraftCheckpoints(prev => prev.map(c => ({ ...c, frequencyType: 'interval', frequencyValue: valueInHours })));
        };

        const showExceedsError = exceedsDuration(freqRawInput, freqUnit);

        const applyStartTime = (value) => {
            setCheckpointStartTime(value);
            if (value && isStartTimeWithinDuty(value)) {
                setDraftCheckpoints(prev => prev.map(c => ({ ...c, from: value })));
            }
        };

        const showStartTimeError = !!checkpointStartTime && !isStartTimeWithinDuty(checkpointStartTime);

        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Clock size={16} style={{ color: 'rgb(13,92,99)' }} />
                        <Text strong style={{ fontSize: '15px' }}>Set Checkpoint Frequency</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {draftCheckpoints.length} checkpoint{draftCheckpoints.length !== 1 ? 's' : ''} selected — all share the same start time & frequency
                        {dutyDurationHours != null && ` (duty length: ${dutyDurationHours.toFixed(2)} hr)`}
                    </Text>
                </div>

                <div style={{ flex: 1, display: 'flex',  justifyContent: 'center', padding: '32px' }}>
                    <div style={{ width: '100%', maxWidth: '420px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '12px', fontWeight: '500' }}>
                                Start checkpoints from
                            </Text>

                            <Input
                                type="time"
                                size="large"
                                value={checkpointStartTime || ''}
                                onChange={(e) => applyStartTime(e.target.value)}
                                style={{ borderRadius: '10px', borderColor: (checkpointStartTime && !showStartTimeError) ? 'rgb(13,92,99)' : undefined }}
                            />

                            {showStartTimeError && (
                                <div style={{ marginTop: '14px', padding: '10px 14px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', fontSize: '12px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    ⚠️ Start time must be on or after shift start ({shiftStartTime}) and within the duty ({shiftStartTime} – {shiftEndTime})
                                </div>
                            )}
                        </div>

                        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '12px', fontWeight: '500' }}>
                                Repeat every
                            </Text>

                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <Input
                                    type="number"
                                    min={1}
                                    step="any"
                                    size="large"
                                    placeholder={freqUnit === 'hours' ? 'e.g. 2' : 'e.g. 90'}
                                    value={freqRawInput}
                                    onChange={(e) => {
                                        setFreqRawInput(e.target.value);
                                        applyFrequency(e.target.value, freqUnit);
                                    }}
                                    style={{ flex: 1, borderRadius: '10px', borderColor: checkpointFrequency.value ? 'rgb(13,92,99)' : undefined }}
                                />

                                {/* Hours / Minutes toggle */}
                                <div style={{ display: 'flex', borderRadius: '10px', border: '1px solid #e8e8e8', overflow: 'hidden', flexShrink: 0 }}>
                                    {['hours'].map(unit => (
                                        <button
                                            key={unit}
                                            onClick={() => {
                                                setFreqUnit(unit);
                                                applyFrequency(freqRawInput, unit);
                                            }}
                                            style={{
                                                padding: '8px 14px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                                                background: freqUnit === unit ? 'rgb(13,92,99)' : 'white',
                                                color: freqUnit === unit ? 'white' : '#666',
                                                transition: 'all 0.15s'
                                            }}
                                        >
                                            {unit === 'hours' ? 'Hrs' : 'Min'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {showExceedsError ? (
                                <div style={{ marginTop: '14px', padding: '10px 14px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', fontSize: '12px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    ⚠️ Repeat interval can't be longer than the duty ({dutyDurationHours?.toFixed(2)} hr)
                                </div>
                            ) : checkpointFrequency.value > 0 && (
                                <div style={{ marginTop: '14px', padding: '10px 14px', background: 'rgba(13,92,99,0.06)', borderRadius: '8px', border: '1px solid rgba(13,92,99,0.2)', fontSize: '12px', color: 'rgb(13,92,99)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Clock size={13} />
                                    Checkpoints will repeat every <strong style={{ marginLeft: '3px' }}>{freqRawInput} {freqUnit === 'minutes' ? 'min' : 'hr'}</strong>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ── Config screens (shared for tasks and checkpoints) ─────────────────────────

    // Only one task may own "shift_start" and only one task may own "shift_end".
    // A task assigned "both_start_end" occupies both slots, so it blocks every other
    // task from taking shift_start, shift_end, or both_start_end.
    const startTakenByOtherTask = draftTasks.some(t => t.taskId !== activeTaskId && (t.assignmentType === 'shift_start' || t.assignmentType === 'both_start_end'));
    const endTakenByOtherTask = draftTasks.some(t => t.taskId !== activeTaskId && (t.assignmentType === 'shift_end' || t.assignmentType === 'both_start_end'));

    const renderActionSelection = () => (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
                <button
                    onClick={clearActiveItem}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#666', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '12px', padding: '4px 0' }}
                >
                    <ArrowLeft size={16} /> Back to list
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Settings size={16} style={{ color: '#1890ff' }} />
                    <Text  style={{ fontSize: '15px' }}>
                        Configure {activeItemType === 'checkpoint' ? 'Checkpoint' : 'Task'}
                    </Text>
                </div>
                <Text strong style={{ fontSize: '16px' }}>{activeItem?.label}</Text>
            </div>

            <div style={{ flex: 1, display: 'flex', justifyContent:'center', padding: '24px' }}>
                <div style={{ width: '100%', maxWidth: '480px' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>When should this task be completed?</div>
                        <div style={{ display: 'grid', gap: '16px' }}>

                            {[
                                { value: 'anytime',        label: 'Any time' },
                                { value: 'shift_start',    label: 'Start of shift',    disabled: checkpointDisabled || startTakenByOtherTask, disabledReason:  checkpointDisabled? "This option is only available for new duty." : 'Another task already owns the start of shift' },
                                { value: 'shift_end',      label: 'End of the shift',  disabled: checkpointDisabled || endTakenByOtherTask, disabledReason:  checkpointDisabled? "This option is only available for new duty." : 'Another task already owns the end of shift' },
                                { value: 'both_start_end', label: 'Both start and end of the shift', disabled: checkpointDisabled || startTakenByOtherTask || endTakenByOtherTask, disabledReason: checkpointDisabled? "This option is only available for new duty." : 'Start or end of shift is already assigned to another task' },
                                { value: 'custom',         label: 'Custom time' },
                            ].map((opt) => {
                                const isSelected = activeItem?.assignmentType === opt.value;
                                const brandColor = 'rgb(13, 92, 99)';
                                const isCustomIncomplete = opt.value === 'custom' && isSelected && !isCustomWindowValid(activeItem?.from, activeItem?.to);
                                const item = (
                                    <div
                                        key={opt.value}
                                        onClick={() => {
                                            if (opt.disabled) return;
                                            updateActiveItemDetails({ assignmentType: opt.value });
                                            if (opt.value === 'custom') setConfigStep('schedule');
                                        }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '12px',
                                            padding: '12px 16px', borderRadius: '10px',
                                            border: `1px solid ${isSelected ? brandColor : '#e8e8e8'}`,
                                            background: opt.disabled ? '#f5f5f5' : (isSelected ? 'rgba(13,92,99,0.06)' : 'white'),
                                            cursor: opt.disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease',
                                            opacity: opt.disabled ? 0.5 : 1,
                                        }}
                                    >
                                        <div style={{
                                            width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                                            border: `2px solid ${isSelected ? brandColor : '#ccc'}`,
                                            background: isSelected ? brandColor : 'white',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            {isSelected && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white' }} />}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <span style={{ fontSize: '13px', fontWeight: isSelected ? '600' : '400', color: isSelected ? brandColor : '#333' }}>
                                                {opt.label}
                                            </span>
                                            {opt.value === 'custom' && isSelected && (
                                                <div style={{ fontSize: '11px', marginTop: '2px', color: isCustomIncomplete ? '#dc2626' : '#666' }}>
                                                    {isCustomIncomplete ? 'Tap to set start & end time' : `${activeItem.from} – ${activeItem.to}`}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                                return opt.disabled ? (
                                    <Tooltip key={opt.value} title={opt.disabledReason}>{item}</Tooltip>
                                ) : item;
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderScheduleConfig = () => (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
                <button onClick={() => setConfigStep('action')} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#666', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '12px', padding: '4px 0' }}>
                    <ArrowLeft size={16} /> Back to options
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Clock size={16} style={{ color: '#FF7A59' }} />
                    <Text strong style={{ fontSize: '15px' }}>Set Schedule</Text>
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>{activeItem?.label}</Text>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div style={{ padding: '32px', maxWidth: '800px', margin: '0 auto' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                                <Text strong style={{ fontSize: '14px' }}>
                                    <ClockCircleOutlined style={{ marginRight: '8px', color: '#f093fb' }} /> Time Window
                                </Text>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '8px', fontWeight: '500' }}>Start Time</Text>
                                    <Input type="time" size="large" value={activeItem?.from || ''} onChange={(e) => updateActiveItemDetails({ from: e.target.value })} status={(activeItem?.from && !activeItem?.to) ? 'error' : ''} style={{ borderRadius: '8px' }} />
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '8px', fontWeight: '500' }}>End Time</Text>
                                    <Input type="time" size="large" value={activeItem?.to || ''} onChange={(e) => updateActiveItemDetails({ to: e.target.value })} status={(!activeItem?.from && activeItem?.to) ? 'error' : ''} style={{ borderRadius: '8px' }} />
                                </div>
                            </div>
                            {((activeItem?.from && !activeItem?.to) || (!activeItem?.from && activeItem?.to)) ? (
                                <div style={{ marginTop: '12px', padding: '10px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', color: '#dc2626', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    ⚠️ Both start and end times are required
                                </div>
                            ) : (activeItem?.from && activeItem?.to && !isCustomWindowValid(activeItem.from, activeItem.to)) && (
                                <div style={{ marginTop: '12px', padding: '10px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', color: '#dc2626', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    ⚠️ End time must be after start time, and both must fall within the duty ({shiftStartTime} – {shiftEndTime})
                                </div>
                            )}
                        </div>

                        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: activeItem?.location?.display || showLocationPicker ? '16px' : 0 }}>
                                <Text strong style={{ fontSize: '14px' }}>
                                    <EnvironmentOutlined style={{ marginRight: '8px', color: '#10b981' }} /> Location Pin
                                </Text>
                                <Tag color="default" style={{ fontSize: '10px' }}>Optional</Tag>
                            </div>
                            {activeItem?.location?.display && (
                                <div style={{ marginBottom: '16px', padding: '12px', background: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0', fontSize: '12px', color: '#065f46', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <MapPin size={14} />
                                        <div>
                                            <strong style={{ display: 'block', fontSize: '11px' }}>Location Set</strong>
                                            <span style={{ fontSize: '11px', opacity: 0.9 }}>{activeItem.location.display}</span>
                                        </div>
                                    </div>
                                    <button onClick={() => setShowLocationPicker(v => !v)} style={{ fontSize: '11px', flexShrink: 0, background: 'none', border: 'none', padding: 0, color: 'rgb(13,92,99)', fontWeight: '600', cursor: 'pointer' }}>
                                        {showLocationPicker ? 'Hide map' : 'Change'}
                                    </button>
                                </div>
                            )}
                            {showLocationPicker ? (
                                <div style={{ borderRadius: '12px', overflow: 'hidden', padding: '20px 30px' }}>
                                    <MapComponent location={location} onLocationChange={handleLocationUpdate} />
                                </div>
                            ) : !activeItem?.location?.display && (
                                <button onClick={() => setShowLocationPicker(true)} className="drawer-outlined-btn" style={{ padding: '8px 16px', fontSize: '12px' }}>
                                    + Add location pin
                                </button>
                            )}
                        </div>

                        <button
                            onClick={() => setConfigStep('action')}
                            className="drawer-filled-btn"
                            style={{ alignSelf: 'flex-start' }}
                        >
                            Save Schedule
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const [selectedLocation, setSelectedLocation] = useState(null);
    const isLocExist = sitegroupLocations?.length > 0 && !selectedLocation || false;

    useEffect(() => {
        if (associatedSiteGroupLocationId && sitegroupLocations?.length > 0) {
            setSelectedLocation(sitegroupLocations?.find((sgl) => sgl?._id == associatedSiteGroupLocationId));
        }
    }, [associatedSiteGroupLocationId, sitegroupLocations]);

    const totalSelected = draftTasks.length + draftCheckpoints.length;

    // Only commit the draft to the parent (and trigger the real submit) when Assign is clicked.
    const onAssignClick = () => {
        if (isSubmitDisabled()) return;
        setSelectedTasks(draftTasks);
        setSelectedCheckpoints(draftCheckpoints);
        if (standAlone) {
            handelSubmit(draftTasks, draftCheckpoints);
        } else {
            close();
        }
    };

    return (
        <>
        <Drawer
            open={visible}
            onClose={close}
            placement="right"
            width={isLocExist ? 700 : '92%'}
            styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
            maskClosable={false}
            footer={
                isLocExist ? null :
                    <div className="drawer-footer" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ color: '#666', fontSize: '13px' }}>
                            {!hasShiftTimes ? (
                                <span style={{ color: '#ff4d4f' }}>Set the duty's shift start & end time before assigning tasks or checkpoints</span>
                            ) : unconfiguredTaskCount > 0 ? (
                                <span style={{ color: '#ff4d4f' }}>
                                    {unconfiguredTaskCount} task{unconfiguredTaskCount > 1 ? 's' : ''} need{unconfiguredTaskCount === 1 ? 's' : ''} to be configured (set "when should this task be completed?") before assigning
                                </span>
                            ) : unconfiguredNfcCheckpoints.length > 0 ? (
                                <span style={{ color: '#ff4d4f' }}>
                                    {unconfiguredNfcCheckpoints.length} checkpoint{unconfiguredNfcCheckpoints.length > 1 ? 's' : ''} {unconfiguredNfcCheckpoints.length > 1 ? 'have' : 'has'} an NFC tag that hasn't been configured yet ({unconfiguredNfcCheckpoints.map(cp => cp.name).join(', ')}) — scan it from the mobile app to set it up (any user can configure a checkpoint for any site), then try again
                                </span>
                            ) : totalSelected > 0 && (
                                <>
                                    {draftTasks.filter(t => t.assignmentType).length > 0 && `${draftTasks.filter(t => t.assignmentType).length} task${draftTasks.filter(t => t.assignmentType).length > 1 ? 's' : ''}`}
                                    {draftTasks.filter(t => t.assignmentType).length > 0 && draftCheckpoints.length > 0 && ', '}
                                    {draftCheckpoints.length > 0 && `${draftCheckpoints.length} checkpoint${draftCheckpoints.length > 1 ? 's' : ''}`}
                                    {' selected'}
                                </>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button disabled={loading} className={loading ? 'disabled-btn drawer-outlined-btn' : 'drawer-outlined-btn'} onClick={close}>
                                Cancel
                            </button>
                            <button
                                onClick={onAssignClick}
                                disabled={loading || isSubmitDisabled()}
                                className={(loading || isSubmitDisabled()) ? 'disabled-btn drawer-filled-btn' : 'drawer-filled-btn'}
                            >
                                {loading ? <Spinner /> : "Assign"}
                            </button>
                        </div>
                    </div>
            }
            closable={false}
        >
            {sitegroupLocations?.length > 0 && !selectedLocation ?
                <LocationSelector locations={sitegroupLocations} onClose={close} onContinue={setSelectedLocation} />
                :
                <React.Fragment>
                    <div className='modal-header-title'>
                        {selectedLocation ?
                            <span onClick={() => setSelectedLocation(null)} style={{ cursor: 'pointer' }}>
                                <FontAwesomeIcon icon={faArrowLeftLong} />&emsp;
                                Assign Tasks & Checkpoints {`(on ${selectedLocation?.address})`}
                            </span>
                            :
                            <span>Assign Tasks &amp; Checkpoints</span>
                        }
                        <span onClick={() => { close(); }}><FontAwesomeIcon className='close' icon={faClose} /></span>
                    </div>

                    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {(siteGroupLocationLoading || taskListLoading || loading) ? (
                            <div style={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                                <Spinner />
                            </div>
                        ) : !hasShiftTimes ? (
                            <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <Clock size={48} style={{ color: '#d9d9d9', marginBottom: '16px' }} />
                                    <Text type="secondary" style={{ fontSize: '14px', display: 'block' }}>Set the duty's shift start & end time first</Text>
                                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>Tasks and checkpoints can only be assigned once the shift time window is set</Text>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

                                {/* ── LEFT PANEL ── */}
                                <div className=" setting-body-inner" style={{ width: '380px', borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                                    {/* Tabs */}
                                    <Tabs
                                        activeKey={activeTab}
                                        onChange={handleTabChange}
                                        // Opened from a single-purpose "Assign Task"/"Assign Checkpoint" button:
                                        // only one tab exists, so there's nothing to switch to - hide the nav
                                        // entirely rather than show a lone, unclickable-feeling tab header.
                                        tabBarStyle={lockInitialTab ? { display: 'none' } : undefined}
                                        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', marginTop: '6px', }}
                                        items={[
                                            {
                                                key: 'tasks',
                                                label: (
                                                    <span>
                                                        Available Tasks
                                                        {draftTasks.filter(t => t.assignmentType).length > 0 && (
                                                            <span style={{ marginLeft: '6px', background: 'rgb(13, 92, 99)', color: 'white', borderRadius: '10px', padding: '1px 7px', fontSize: '11px' }}>
                                                                {draftTasks.filter(t => t.assignmentType).length}
                                                            </span>
                                                        )}
                                                    </span>
                                                ),
                                                children: (
                                                    <div style={{ overflowY: 'auto', height: '100%' }}>
                                                        <div style={{ padding: '12px 20px 8px', backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1 }}>
                                                            <Text strong style={{ fontSize: '13px' }}>Available Tasks</Text>
                                                            {taskList?.length > 0 && (
                                                                <Checkbox
                                                                    checked={draftTasks.length === taskList.length}
                                                                    indeterminate={draftTasks.length > 0 && draftTasks.length < taskList.length}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setDraftTasks(taskList.map(t => ({
                                                                                taskId: t._id, label: t.label,
                                                                                assignmentType: null, priority: null,
                                                                                associatedSiteId: null, from: null, to: null, location: null,
                                                                                associatedSiteGroupLocationId: selectedLocation ? selectedLocation?._id : null
                                                                            })));
                                                                        } else {
                                                                            setDraftTasks([]);
                                                                            if (activeItemType === 'task') clearActiveItem();
                                                                        }
                                                                    }}
                                                                >
                                                                    <span style={{ fontSize: '12px' }}>Select all</span>
                                                                </Checkbox>
                                                            )}
                                                        </div>

                                                        <div className="link" style={{display:'flex', justifyContent:'right', padding:'10px 0', cursor:'pointer'}} onClick={() => setCreateTaskDrawerVisible(true)}>Create new Task <ExternalLink/> </div>

                                                        <div style={{ padding: '8px' }}>
                                                            {!siteId ? (
                                                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No site associated with this duty" style={{ marginTop: '20px' }} />
                                                            ) : taskList?.length > 0 ? taskList.map((task) => {
                                                                const selectedTask = draftTasks.find(t => t.taskId === task._id);
                                                                const isSelected = !!selectedTask;
                                                                const isConfigured = isSelected && !!selectedTask.assignmentType;
                                                                const isActive = activeTaskId === task._id && activeItemType === 'task';
                                                                return (
                                                                    <div
                                                                        key={task._id}
                                                                        onClick={() => {
                                                                            if (!isSelected) {
                                                                                handleTaskSelection(task._id, task.label, true);
                                                                            } else {
                                                                                setActiveTaskId(task._id);
                                                                                setActiveCheckpointId(null);
                                                                                setActiveItemType('task');
                                                                                setConfigStep('action');
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            padding: '12px', marginBottom: '6px',
                                                                            border: `2px solid ${isActive ? 'rgb(13,92,99)' : isConfigured ? 'rgb(13,92,99)' : isSelected ? '#faad14' : '#f0f0f0'}`,
                                                                            borderRadius: '12px',
                                                                            backgroundColor: isSelected ? (isActive ? 'rgba(13,92,99,0.08)' : isConfigured ? 'rgba(13,92,99,0.05)' : '#fffbe6') : 'white',
                                                                            cursor: 'pointer', transition: 'all 0.2s ease',
                                                                            boxShadow: isActive ? '0 2px 8px rgba(13,92,99,0.15)' : 'none'
                                                                        }}
                                                                    >
                                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                                                            {isConfigured && (
                                                                                <Checkbox checked onClick={(e) => { e.stopPropagation(); handleTaskSelection(task._id, task.label, false); }} style={{ marginTop: '2px' }} />
                                                                            )}
                                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                                <div style={{ fontWeight: '500', marginBottom: '4px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{task.label}</span>
                                                                                    {task?.flow && <Tooltip title="Form attached"><FileText size={14} style={{ color: '#1890ff', flexShrink: 0 }} /></Tooltip>}
                                                                                </div>
                                                                                <div style={{ fontSize: '11px', color: '#999', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                                    <BranchesOutlined style={{ fontSize: '11px' }} />
                                                                                    {task.flow === "formBase" ? "Form template" : "Field base"}
                                                                                </div>
                                                                                {isSelected && getItemSummary(selectedTask)}
                                                                            </div>
                                                                            {isSelected && <ChevronRight size={16} style={{ color: isActive ? 'rgb(13,92,99)' : '#999', flexShrink: 0, marginTop: '2px' }} />}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }) : (
                                                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No available tasks" style={{ marginTop: '20px' }} />
                                                            )}
                                                        </div>
                                                    </div>
                                                ),
                                            },
                                            checkpointDisabled==false &&
                                            {
                                                key: 'checkpoints',
                                                label: (
                                                    <span>
                                                        Checkpoints
                                                        {draftCheckpoints.length > 0 && (
                                                            <span style={{ marginLeft: '6px', background: 'rgb(13, 92, 99)', color: 'white', borderRadius: '10px', padding: '1px 7px', fontSize: '11px' }}>
                                                                {draftCheckpoints.length}
                                                            </span>
                                                        )}
                                                    </span>
                                                ),
                                                children: (
                                                    <div style={{ overflowY: 'auto', height: '100%' }}>
                                                        <div style={{ padding: '12px 20px 8px', backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1 }}>
                                                            <Text strong style={{ fontSize: '13px' }}>
                                                                Checkpoints
                                                                {freqRawInput && checkpointFrequency.type && draftCheckpoints.length > 0 && (
                                                                    <Tag color="cyan" style={{ marginLeft: '8px', fontSize: '11px' }}>
                                                                        Every {freqRawInput} {freqUnit === 'minutes' ? 'min' : 'hr'}
                                                                    </Tag>
                                                                )}
                                                            </Text>
                                                            {checkpointList.length > 0 && (
                                                                <Checkbox
                                                                    checked={draftCheckpoints.length === checkpointList.length}
                                                                    indeterminate={draftCheckpoints.length > 0 && draftCheckpoints.length < checkpointList.length}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            const freq = checkpointFrequency.type ? { frequencyType: checkpointFrequency.type, frequencyValue: checkpointFrequency.value } : {};
                                                                            const startTime = checkpointStartTime || shiftStartTime;
                                                                            setDraftCheckpoints(checkpointList.map(cp => ({
                                                                                checkpointId: cp._id, label: cp.name,
                                                                                associatedSiteGroupLocationId: selectedLocation ? selectedLocation?._id : null,
                                                                                from: startTime,
                                                                                ...freq
                                                                            })));
                                                                            setCheckpointStartTime(startTime);
                                                                            setActiveItemType('checkpoint');
                                                                            setConfigStep('checkpoint-frequency');
                                                                        } else {
                                                                            setDraftCheckpoints([]);
                                                                            setCheckpointFrequency({ type: null, value: null });
                                                                            setCheckpointStartTime(null);
                                                                            if (activeItemType === 'checkpoint') clearActiveItem();
                                                                        }
                                                                    }}
                                                                >
                                                                    <span style={{ fontSize: '12px' }}>Select all</span>
                                                                </Checkbox>
                                                            )}
                                                        </div>
                                                        {checkpointList.length > 0 && (
                                                            <div style={{ padding: '6px 20px', fontSize: '11px', color: '#999' }}>
                                                                Checkpoints you select here all patrol on the same start time & repeat frequency, set once for the whole group.
                                                            </div>
                                                        )}

                                                        <div className="link" style={{display:'flex', justifyContent:'right', padding:'10px 0', cursor:'pointer'}} onClick={() => setCreateCheckpointDrawerVisible(true)}>Create new Checkpoint <ExternalLink/> </div>

                                                        <div style={{ padding: '8px' }}>
                                                            {!siteId ? (
                                                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No site associated with this duty" style={{ marginTop: '20px' }} />
                                                            ) : checkpointLoading ? (
                                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '120px' }}>
                                                                    <Spinner />
                                                                </div>
                                                            ) : checkpointList.length > 0 ? checkpointList.map((cp) => {
                                                                const isSelected = draftCheckpoints.some(c => c.checkpointId === cp._id);
                                                                const isActive = isSelected && activeItemType === 'checkpoint' && configStep === 'checkpoint-frequency';
                                                                return (
                                                                    <div
                                                                        key={cp._id}
                                                                        onClick={() => { if (isSelected) { setActiveItemType('checkpoint'); setConfigStep('checkpoint-frequency'); } }}
                                                                        style={{
                                                                            padding: '12px', marginBottom: '6px',
                                                                            border: `2px solid ${isActive ? 'rgb(13,92,99)' : isSelected ? 'rgb(13,92,99)' : '#f0f0f0'}`,
                                                                            borderRadius: '12px',
                                                                            backgroundColor: isSelected ? (isActive ? 'rgba(13,92,99,0.08)' : 'rgba(13,92,99,0.05)') : 'white',
                                                                            cursor: isSelected ? 'pointer' : 'default', transition: 'all 0.2s ease',
                                                                            boxShadow: isActive ? '0 2px 8px rgba(13,92,99,0.15)' : 'none'
                                                                        }}
                                                                    >
                                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                                                            <Checkbox
                                                                                checked={isSelected}
                                                                                onChange={(e) => { e.stopPropagation(); handleCheckpointSelection(cp, e.target.checked); }}
                                                                                style={{ marginTop: '2px' }}
                                                                            />
                                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                                <div style={{ fontWeight: '500', marginBottom: '4px', fontSize: '13px' }}>
                                                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{cp.name}</span>
                                                                                </div>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: '600', color: '#0e7490', background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: '6px', padding: '2px 6px' }}>
                                                                                        <MapPinnedIcon size={10} /> Geofence
                                                                                    </span>
                                                                                    {cp.tagType === 'qr' && (
                                                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: '600', color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '6px', padding: '2px 6px' }}>
                                                                                            <QrCode size={10} /> QR
                                                                                        </span>
                                                                                    )}
                                                                                    {cp.tagType === 'nfc' && (
                                                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: '600', color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: '6px', padding: '2px 6px' }}>
                                                                                            <Nfc size={10} /> NFC
                                                                                        </span>
                                                                                    )}
                                                                                    {cp.tagType === 'nfc' && !cp.nfcTagId && (
                                                                                        <Tooltip title="This checkpoint's NFC tag hasn't been scanned/configured yet from the mobile app - it can't be assigned to a duty until it is. Any user can configure a checkpoint for any site.">
                                                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: '600', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '2px 6px' }}>
                                                                                                <AlertTriangle size={10} /> Not configured
                                                                                            </span>
                                                                                        </Tooltip>
                                                                                    )}
                                                                                    {cp.responseFlow === 'form' && (
                                                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: '600', color: '#0f766e', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '2px 6px' }}>
                                                                                            <FileText size={10} /> Form
                                                                                        </span>
                                                                                    )}
                                                                                    {cp.responseFlow === 'fields' && (
                                                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: '600', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '2px 6px' }}>
                                                                                            <List size={10} /> Custom Fields
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                {isSelected && getItemSummary({}, true)}
                                                                            </div>
                                                                            {isSelected && <ChevronRight size={16} style={{ color: isActive ? 'rgb(13,92,99)' : '#999', flexShrink: 0, marginTop: '2px' }} />}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }) : (
                                                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No checkpoints available for this site" style={{ marginTop: '20px' }} />
                                                            )}
                                                        </div>
                                                    </div>
                                                ),
                                            },
                                        ].filter(item => item && (!lockInitialTab || item.key === initialTab))}

                                        className='grid-tabs'
                                    />

                                </div>

                                {/* ── RIGHT PANEL ── */}
                                {configStep === 'select' || (configStep !== 'checkpoint-frequency' && !activeItem) ? (
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <Settings size={48} style={{ color: '#d9d9d9', marginBottom: '16px' }} />
                                            <Text type="secondary" style={{ fontSize: '14px', display: 'block' }}>Select a task or checkpoint to configure</Text>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>Choose an item from the list to get started</Text>
                                        </div>
                                    </div>
                                ) : configStep === 'checkpoint-frequency' ? renderCheckpointFrequencyConfig()
                                    : configStep === 'action' ? renderActionSelection()
                                        : configStep === 'schedule' ? renderScheduleConfig()
                                            : null}
                            </div>
                        )}
                    </div>
                </React.Fragment>
            }
        </Drawer>
        {createTaskDrawerVisible &&
            <CreateTaskFieldDrawer
                visible={createTaskDrawerVisible}
                objectType={"Task"}
                taskListRefetch={refetchTaskList}
                onClose={() => { refetchTaskList?.(); setCreateTaskDrawerVisible(false); }}
                preselectedSite={currentSiteData?.site?.response}
            />
        }
        {createCheckpointDrawerVisible &&
            <CheckpointDrawer
                visible={createCheckpointDrawerVisible}
                close={() => setCreateCheckpointDrawerVisible(false)}
                refetch={refetchCheckpointList}
                siteId={siteId}
            />
        }
        </>
    );
};
